import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
  Text,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  listPerson,
  setPersonPage,
  selectPerson,
  downloadContactsCsvTemplate,
  checkContactsCsvUploadStatus,
  resetCsvUploadData,
  uploadContactsCsvFile,
  importContactsFromCsv,
  getContactsCsvUploadMatches,
  cancelContactUploadFromCsv,
  resetPersonList,
} from "./personDbSlice";
import {
  downloadFile,
  isLoading,
  isUploadSuccess,
} from "../../../common/helper/commonHelper";
import SpinnerContainer from "../../../components/SpinnerContainer";
import { ImportReq, Person } from "../../../common/types/person";
import urls from "../../../urls";
import { FaChevronDown } from "react-icons/fa";
import CreatePersonModal from "./components/CreatePersonModal";
import { debounce } from "lodash";
import { SearchInputAndCreateButton } from "../../../components/CommonListHeader";
import UploadCSVModal from "../../../components/UploadCsvModal";
import { useAppDispatch } from "../../../store";
import CsvUploadConfigModal from "./components/csvUpload/CsvUploadConfigModal";
import DropZoneText from "./components/csvUpload/DropZoneText";
import { DataTable } from "../../../components/data-table/DataTable";
import { createColumnHelper } from "@tanstack/react-table";
import LayoutWithTable from "../../../layout/LayoutWithTable";
import { FormatDate } from "../../../components/DateTimeRangeFilter";
import NumberFormatHeader from "../../../components/NumberFormatHeader";

enum ACTIONS {
  CREATE = "create",
  IMPORT = "import",
}

function ActionsMenu({ onClick }: { onClick: (action: ACTIONS) => void }) {
  return (
    <Menu>
      <MenuButton
        as={Button}
        size="sm"
        colorScheme="blue"
        rightIcon={<FaChevronDown />}
      >
        Actions
      </MenuButton>
      <MenuList zIndex="3">
        <MenuItem onClick={() => onClick(ACTIONS.CREATE)}>
          Create Contact
        </MenuItem>
        <MenuItem onClick={() => onClick(ACTIONS.IMPORT)}>
          Import Contacts
        </MenuItem>
      </MenuList>
    </Menu>
  );
}

export default function Persons() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    isOpen: isCsvModalOpen,
    onClose: onCsvModalClose,
    onOpen: onCsvModalOpen,
  } = useDisclosure();

  const {
    isOpen: isCSVConfigModalOpen,
    onClose: onCsvConfigModalClose,
    onOpen: onCsvConfigModalOpen,
  } = useDisclosure();

  const {
    isOpen: isCreateModalOpen,
    onClose: onCreateModalClose,
    onOpen: onCreateModalOpen,
  } = useDisclosure();

  const { person, csvUpload } = useSelector(selectPerson);

  const [searchKeyword, setSearchKeyword] = useState("");
  const filterColumns = ["email", "first_name", "last_name"];

  const columnHelper = createColumnHelper<Person>();

  const columns = useMemo(
    () => [
      columnHelper.accessor("email", {
        header: "Email address",
        size: 300,
      }),
      columnHelper.accessor("first_name", {
        header: "First Name",
        size: 250,
      }),
      columnHelper.accessor("last_name", {
        header: "Last Name",
        size: 250,
      }),
      columnHelper.accessor("title", {
        header: "Title",
        cell: (info) => (
          <Text whiteSpace="pre-line" wordBreak="break-word">
            {info.getValue()}
          </Text>
        ),
        size: 250,
      }),
      columnHelper.accessor("company_name", {
        header: "Company",
        size: 250,
      }),
      columnHelper.accessor("phone_number", {
        header: "Phone",
        size: 200,
      }),
      columnHelper.accessor("person_created_time", {
        header: "Created Date",
        cell: (info) => <FormatDate date={info.getValue()} />,
        size: 200,
      }),
    ],
    [columnHelper]
  );

  const searchIfValid = useCallback(
    (searchKeyword: string) => {
      if (person.currentPageNo !== 1) {
        dispatch(setPersonPage(1));
      } else {
        dispatch(listPerson({ searchKeyword, filterColumns }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, person.currentPageNo]
  );

  const debouncedSearch = useMemo(
    () => debounce(searchIfValid, 1000),
    [searchIfValid]
  );

  function onSearchKeywordChange(text: string) {
    setSearchKeyword(text);
    debouncedSearch(text);
  }

  const openPerson = useCallback(
    (person: Person) => {
      navigate(`${urls.person}/${person.id}`);
    },
    [navigate]
  );

  function insertContacts(config: Omit<ImportReq, "filepath">) {
    dispatch(
      importContactsFromCsv({
        filepath: csvUpload.filepath,
        ...config,
      })
    );
  }

  useEffect(() => {
    dispatch(listPerson({ searchKeyword, filterColumns }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, person.currentPageNo]);

  useEffect(() => {
    return () => {
      dispatch(resetPersonList());
    };
  }, [dispatch]);

  useEffect(() => {
    if (csvUpload.matches) {
      // Delay added to let the upload animation complete
      setTimeout(() => {
        onCsvConfigModalOpen();
      }, 500);

      setTimeout(() => {
        onCsvModalClose();
      }, 500);
    }
  }, [onCsvModalClose, onCsvConfigModalOpen, csvUpload.matches]);

  useEffect(() => {
    if (isUploadSuccess(csvUpload.uploadStatus?.status)) {
      dispatch(getContactsCsvUploadMatches(csvUpload.filepath ?? ""));
    }
  }, [csvUpload.uploadStatus, csvUpload.filepath, dispatch]);

  function actionsClickHandler(action: ACTIONS) {
    switch (action) {
      case ACTIONS.CREATE:
        onCreateModalOpen();
        break;
      case ACTIONS.IMPORT:
        onCsvModalOpen();
        break;
    }
  }

  async function downloadTemplate() {
    const response = await dispatch(downloadContactsCsvTemplate());
    if (response.payload) {
      const template = response.payload as Blob;
      downloadFile(template, "file.csv");
    }
  }

  function checkUploadStatus() {
    if (csvUpload.filepath) {
      dispatch(checkContactsCsvUploadStatus(csvUpload.filepath));
    }
  }

  function upload(file: File[]) {
    dispatch(resetCsvUploadData());
    dispatch(uploadContactsCsvFile(file[0]));
  }

  function cancelUpload() {
    if (csvUpload.filepath) {
      dispatch(cancelContactUploadFromCsv(csvUpload.filepath));
    }
    dispatch(resetCsvUploadData());
  }

  function uploadAgain() {
    resetCsvUploadData();
    onCsvModalOpen();
    onCsvConfigModalClose();
  }

  return (
    <>
      <NumberFormatHeader
        heading="Contacts"
        count={person.count ? person.count : null}
        countLabel="contacts"
      >
        <SearchInputAndCreateButton
          searchInputProps={{
            placeholder: "Search contacts ",
            name: "search-input",
            value: searchKeyword,
            onSearch: onSearchKeywordChange,
          }}
          ctaMenu={<ActionsMenu onClick={actionsClickHandler} />}
        />
      </NumberFormatHeader>

      <SpinnerContainer
        height="350px"
        loading={
          (person.fetchingList && !person.changingPage) ||
          person.totalPageCount === null
        }
      >
        <LayoutWithTable>
          <DataTable
            loading={person.fetchingList}
            list={person.list}
            totalPageCount={person.totalPageCount}
            currentPage={person.currentPageNo}
            onRowClick={openPerson}
            setPage={(pageNo) => dispatch(setPersonPage(pageNo))}
            columns={columns}
            emptyMsg={`No contacts found.${
              searchKeyword ? " Please change the search / filter values" : ""
            }`}
            links={{ to: urls.person, editParam: "id" }}
          />
        </LayoutWithTable>
      </SpinnerContainer>
      <CreatePersonModal
        isOpen={isCreateModalOpen}
        onClose={onCreateModalClose}
      />
      <UploadCSVModal
        modalHeader="Upload CSV file"
        isOpen={isCsvModalOpen}
        onClose={() => {
          cancelUpload();
          onCsvModalClose();
        }}
        downloadTemplateHandler={downloadTemplate}
        upload={upload}
        checkUploadStatus={checkUploadStatus}
        resetData={() => dispatch(resetCsvUploadData())}
        filepath={csvUpload.filepath}
        fetchingUploadStatus={csvUpload.fetchingUploadStatus}
        fileUploadStatus={csvUpload.uploadStatus}
        isDownloadingTemplate={isLoading(csvUpload.downloading)}
        dropzoneContent={<DropZoneText />}
      />

      <CsvUploadConfigModal
        data={csvUpload}
        proceed={insertContacts}
        isOpen={isCSVConfigModalOpen}
        onClose={(cancel) => {
          if (cancel) {
            cancelUpload();
          } else {
            dispatch(resetCsvUploadData());
            dispatch(listPerson({ searchKeyword, filterColumns }));
          }
          onCsvConfigModalClose();
        }}
        uploadAgain={uploadAgain}
      />
    </>
  );
}
